import type {
  PropertyInformation,
  UpdateAddressPropertyInfoInput,
} from '@/gql/graphql';
import { PropertyInfoFormValues } from './propertyInfoSchema';

export type PropertyInfoSeed = Partial<
  Pick<
    PropertyInformation,
    | 'propertyType'
    | 'builtForm'
    | 'constructionAgeBand'
    | 'floorAreaM2'
    | 'numBedrooms'
    | 'heatingSystem'
    | 'heatingEnergyUseKwh'
    | 'electricityOccupancyArchetype'
    | 'annualElectricityConsumptionKwh'
    | 'wallConstruction'
    | 'wallInsulation'
    | 'windowGlazingType'
    | 'loftInsulationMm'
    | 'numStoreys'
  >
>;

/** Whether the property info already has any building fabric details set. */
export const hasFabricDetails = (propertyInfo?: PropertyInfoSeed): boolean =>
  !!(
    propertyInfo &&
    (propertyInfo.wallConstruction ||
      propertyInfo.wallInsulation ||
      propertyInfo.windowGlazingType ||
      propertyInfo.loftInsulationMm != null ||
      propertyInfo.numStoreys != null)
  );

/** Seed react-hook-form default values from an address's existing property info. */
export const propertyInfoDefaultValues = (
  propertyInfo?: PropertyInfoSeed
): Partial<PropertyInfoFormValues> => ({
  propertyType: propertyInfo?.propertyType ?? undefined,
  builtForm: propertyInfo?.builtForm ?? undefined,
  constructionAgeBand: propertyInfo?.constructionAgeBand ?? undefined,
  floorAreaM2:
    propertyInfo?.floorAreaM2 != null ? String(propertyInfo.floorAreaM2) : '',
  numBedrooms:
    propertyInfo?.numBedrooms != null ? String(propertyInfo.numBedrooms) : '',
  heatingSystem: propertyInfo?.heatingSystem ?? undefined,
  heatingEnergyUseKwh:
    propertyInfo?.heatingEnergyUseKwh != null
      ? String(propertyInfo.heatingEnergyUseKwh)
      : undefined,
  electricityOccupancyArchetype:
    propertyInfo?.electricityOccupancyArchetype ?? undefined,
  annualElectricityConsumptionKwh:
    propertyInfo?.annualElectricityConsumptionKwh ?? null,
  wallConstruction: propertyInfo?.wallConstruction ?? undefined,
  wallInsulation: propertyInfo?.wallInsulation ?? undefined,
  windowGlazingType: propertyInfo?.windowGlazingType ?? undefined,
  loftInsulationMm:
    propertyInfo?.loftInsulationMm != null
      ? String(propertyInfo.loftInsulationMm)
      : '',
  numStoreys:
    propertyInfo?.numStoreys != null
      ? String(propertyInfo.numStoreys)
      : undefined,
});

/** Map validated form values to the updateAddressPropertyInfo mutation input. */
export const buildUpdateAddressPropertyInfoInput = (
  addressId: string,
  data: PropertyInfoFormValues
): UpdateAddressPropertyInfoInput => ({
  addressId,
  propertyType: data.propertyType,
  builtForm: data.builtForm,
  // Optional fields send an explicit null when cleared so the form's visible
  // state becomes the saved state (the form always shows every field).
  constructionAgeBand: data.constructionAgeBand || null,
  floorAreaM2: parseFloat(data.floorAreaM2),
  numBedrooms: parseInt(data.numBedrooms, 10),
  heatingSystem: data.heatingSystem,
  heatingEnergyUseKwh: data.heatingEnergyUseKwh
    ? parseFloat(data.heatingEnergyUseKwh)
    : null,
  electricityOccupancyArchetype: data.electricityOccupancyArchetype || null,
  annualElectricityConsumptionKwh: data.annualElectricityConsumptionKwh ?? null,
  wallConstruction: data.wallConstruction || null,
  wallInsulation: data.wallInsulation || null,
  windowGlazingType: data.windowGlazingType || null,
  loftInsulationMm: data.loftInsulationMm
    ? parseInt(data.loftInsulationMm, 10)
    : null,
  numStoreys: data.numStoreys ? parseInt(data.numStoreys, 10) : null,
});
